import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Confetti, Gift, ArrowRight, Coins, Flag, TextT } from "@phosphor-icons/react";

const TYPE_META = {
  credits: { label: "Credits", Icon: Coins, tint: "from-blue-500 to-indigo-700" },
  banner_impressions: { label: "Banner Impressions", Icon: Flag, tint: "from-fuchsia-500 to-pink-700" },
  text_ad_impressions: { label: "Text Ad Impressions", Icon: TextT, tint: "from-cyan-500 to-teal-700" },
};

/**
 * Prize claim page — shown between surfs when the user has hit an active
 * milestone. Every eligibility check and reward application happens on the
 * server; the client only shows what the server tells it to.
 */
export default function SurfPrize() {
  const nav = useNavigate();
  const [pending, setPending] = useState(null);
  const [claimed, setClaimed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const claimInFlight = useRef(false); // extra client-side double-click guard

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/prize/pending");
        if (!alive) return;
        if (!data?.pending) {
          // Nothing to claim — bounce straight back to surfing.
          nav("/surf", { replace: true });
          return;
        }
        setPending(data.pending);
      } catch (e) {
        toast.error(formatApiError(e));
        nav("/surf", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [nav]);

  const claim = async () => {
    if (busy || claimInFlight.current) return;
    claimInFlight.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/prize/claim");
      setClaimed(data);
      toast.success(`Prize claimed: +${Number(data.amount).toLocaleString()} ${TYPE_META[data.prize_type]?.label || data.prize_type}`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg === "Prize already claimed" || msg === "No prize to claim") {
        toast.info("This prize has already been claimed.");
        nav("/surf", { replace: true });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
      claimInFlight.current = false;
    }
  };

  const continueSurfing = () => nav("/surf", { replace: true });

  if (loading || !pending) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950 text-white" data-testid="prize-page-loading">
        <div className="text-sm text-white/70">Loading your prize…</div>
      </div>
    );
  }

  const meta = TYPE_META[pending.prize_type] || { label: pending.prize_type, Icon: Gift, tint: "from-yellow-400 to-orange-600" };
  const Icon = meta.Icon;
  const showClaimed = !!claimed;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 text-white p-4" data-testid="prize-page">
      {/* Decorative floating confetti icons */}
      <Confetti size={60} weight="fill" className="absolute top-10 left-10 text-white/40 rotate-12" />
      <Confetti size={45} weight="fill" className="absolute top-20 right-16 text-white/40 -rotate-12" />
      <Confetti size={50} weight="fill" className="absolute bottom-14 left-20 text-white/40 rotate-45" />

      <div className="relative w-full max-w-lg rounded-3xl bg-white text-slate-900 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className={`bg-gradient-to-br ${meta.tint} p-8 text-center text-white`}>
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-white/20 backdrop-blur-sm mb-4">
            <Icon size={44} weight="fill" />
          </div>
          <div className="text-xs font-black uppercase tracking-[0.3em] text-white/80">Milestone reached</div>
          <div className="text-4xl sm:text-5xl font-black mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            {showClaimed ? "🎉 Prize claimed!" : "🎁 Congratulations!"}
          </div>
        </div>

        <div className="p-8 text-center">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-1">You've surfed</div>
          <div className="text-lg text-slate-800">
            <span className="font-black">{Number(pending.total_surfs).toLocaleString()}</span> sites and reached the{" "}
            <span className="font-black">{Number(pending.milestone).toLocaleString()}</span>-site milestone
          </div>

          <div className="my-6 rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Your prize</div>
            <div className="mt-1 text-5xl font-black text-slate-900 inline-flex items-baseline gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }} data-testid="prize-amount">
              {Number(pending.amount).toLocaleString()}
              <span className="text-xl font-bold text-slate-600">{meta.label}</span>
            </div>
            {pending.name && (
              <div className="mt-2 text-sm font-bold text-slate-700" data-testid="prize-name">{pending.name}</div>
            )}
            {pending.recurring && (
              <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded-full inline-block px-3 py-1">Recurring reward</div>
            )}
          </div>

          {!showClaimed ? (
            <button
              data-testid="prize-claim-btn"
              onClick={claim}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-black px-8 py-3.5 text-base shadow-lg disabled:opacity-60 transition-colors duration-200"
            >
              <Gift size={18} weight="fill" /> {busy ? "Claiming…" : "Claim your prize"}
            </button>
          ) : (
            <button
              data-testid="prize-continue-btn"
              onClick={continueSurfing}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-black px-8 py-3.5 text-base shadow-lg transition-colors duration-200"
            >
              Continue surfing <ArrowRight size={18} weight="bold" />
            </button>
          )}

          <div className="mt-4 text-[11px] text-slate-500">
            {showClaimed
              ? "Your reward has been credited to your account."
              : "Server verifies eligibility and applies the reward atomically."}
          </div>
        </div>
      </div>
    </div>
  );
}
